import React from 'react';
import { RecurrenceInterval } from '../types';
import type { FormData } from '../utils/formUtils';
import type { TaskFieldSettings } from '../hooks/useTaskFieldSettings';

interface TodoFormSecondaryFieldsProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  fieldSettings: TaskFieldSettings;
  showMoreFields: boolean;
  setShowMoreFields: (value: boolean) => void;
}

const TodoFormSecondaryFields: React.FC<TodoFormSecondaryFieldsProps> = ({
  formData,
  setFormData,
  fieldSettings,
  showMoreFields,
  setShowMoreFields,
}) => {
  // Count populated secondary fields that are hidden
  const secondaryFieldsPopulated = [
    fieldSettings.estimatedTime && !!formData.estimated_time_minutes,
    fieldSettings.recurrence && formData.recurrence_interval !== RecurrenceInterval.NEVER,
    fieldSettings.notes && !!formData.notes,
    fieldSettings.parentTask && !!formData.parent_id,
  ].filter(Boolean).length;

  // Auto-show if any secondary field has a value
  const shouldShow = showMoreFields || secondaryFieldsPopulated > 0;

  return (
    <>
      {!shouldShow && (
        <button
          type="button"
          onClick={() => setShowMoreFields(true)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          More fields
          {secondaryFieldsPopulated > 0 && (
            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs">
              {secondaryFieldsPopulated}
            </span>
          )}
        </button>
      )}

      {shouldShow && (
        <>
          {/* Collapse button */}
          {secondaryFieldsPopulated === 0 && (
            <button
              type="button"
              onClick={() => setShowMoreFields(false)}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
              </svg>
              Fewer fields
            </button>
          )}

          {/* Row: Recurrence | Estimated Time */}
          <div className="grid grid-cols-2 gap-3">
            {fieldSettings.recurrence && (
              <div title="On completion, a new task will be created for the next interval" className="cursor-help">
                <label className="block text-xs font-medium text-gray-600 mb-1">Recurrence</label>
                <select
                  value={formData.recurrence_interval}
                  onChange={(e) => setFormData({ ...formData, recurrence_interval: e.target.value as RecurrenceInterval })}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value={RecurrenceInterval.NEVER}>Never</option>
                  <option value={RecurrenceInterval.DAILY}>Daily</option>
                  <option value={RecurrenceInterval.WEEKLY}>Weekly</option>
                  <option value={RecurrenceInterval.MONTHLY}>Monthly</option>
                  <option value={RecurrenceInterval.YEARLY}>Yearly</option>
                </select>
              </div>
            )}

            {fieldSettings.estimatedTime && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Time</label>
                <input
                  type="text"
                  value={formData.estimated_time_minutes}
                  onChange={(e) => setFormData({ ...formData, estimated_time_minutes: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., 30 min"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          {fieldSettings.notes && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input-field resize-none"
                rows={3}
                placeholder="Add notes..."
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TodoFormSecondaryFields;
